import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Send as PostIcon,
  Block as VoidIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { BankAccountPicker, CustomerPicker } from '../LedgerPickers';
import { useLedgerLookups } from '../Journals';
import { ArStatusChip, fmtDate, getErrorMessage, unwrap, unwrapList } from './ArShared';

const STATUSES = ['DRAFT', 'APPROVED', 'POSTED', 'VOID'];

const studentLabel = (p, look) => {
  if (p?.customer) return p.customer.name || `Customer ${p.customer_id}`;
  return look.customerLabel(p?.customer_id, `Customer ${p?.customer_id ?? ''}`);
};

const emptyForm = () => ({
  customer_id: '', payment_date: new Date().toISOString().slice(0, 10),
  amount_received: '', received_into_account_id: '',
});

const defaultApi = {
  list: ledgerAPI.getReceivablePayments,
  get: ledgerAPI.getReceivablePayment,
  create: ledgerAPI.createReceivablePayment,
  approve: ledgerAPI.approveReceivablePayment,
  post: ledgerAPI.postReceivablePayment,
  void: ledgerAPI.voidReceivablePayment,
  remove: ledgerAPI.deleteReceivablePayment,
};

// Student receipts: a customer payment allocated across their open
// invoices (see SchoolAccounting::recordPayment - fee payments already
// allocate this way automatically; this dialog is for one-off / manual
// receipting the fee flow doesn't cover). DRAFT -> APPROVED -> POSTED;
// approve/void run as checker, create/post as maker.
//
// `resource`/`api`/`listInvoices` let FeesBilling.js reuse this exact
// screen against the 'fees' gateway section - see InvoicesTab.js for the
// same pattern and config/ledger.php for the section split.
const PaymentsTab = ({
  resource = RESOURCES.LEDGER.RECEIVABLES,
  api = defaultApi,
  listInvoices = ledgerAPI.getReceivableInvoices,
}) => {
  const { can } = useAccessControl();
  const canCreate = can(resource, ACTIONS.CREATE);
  const canApprove = can(resource, ACTIONS.APPROVE);
  const canDelete = can(resource, ACTIONS.DELETE);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [openInvoices, setOpenInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [allocations, setAllocations] = useState({}); // invoice_id -> amount string
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [voidDlg, setVoidDlg] = useState({ open: false, item: null, void_reason: '' });

  const reload = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    if (!values.customer_id) {
      setOpenInvoices([]);
      setAllocations({});
      return;
    }
    let alive = true;
    setLoadingInvoices(true);
    listInvoices({ customer_id: values.customer_id, limit: 100 })
      .then((res) => {
        if (!alive) return;
        const list = unwrapList(res).filter((inv) => Number(inv.balance_due) > 0);
        setOpenInvoices(list);
        setAllocations({});
      })
      .catch(() => { if (alive) setOpenInvoices([]); })
      .finally(() => { if (alive) setLoadingInvoices(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.customer_id]);

  const setAllocation = (invoiceId, amount) => setAllocations((p) => ({ ...p, [invoiceId]: amount }));
  const totalAllocated = Object.values(allocations).reduce((s, v) => s + (Number(v) || 0), 0);

  const submitCreate = async () => {
    if (!values.customer_id) {
      showSnackbar('Student is required', 'error');
      return;
    }
    if (!(Number(values.amount_received) > 0)) {
      showSnackbar('Amount received must be greater than zero', 'error');
      return;
    }
    if (!values.received_into_account_id) {
      showSnackbar('Bank/cash account is required', 'error');
      return;
    }
    const allocationRows = Object.entries(allocations)
      .filter(([, amt]) => Number(amt) > 0)
      .map(([invoice_id, amt]) => ({ invoice_id: Number(invoice_id), allocated_amount: Number(amt) }));
    if (allocationRows.length < 1) {
      showSnackbar('Allocate the receipt to at least one open invoice', 'error');
      return;
    }
    const payload = {
      customer_id: Number(values.customer_id),
      payment_date: values.payment_date,
      amount_received: Number(values.amount_received),
      received_into_account_id: Number(values.received_into_account_id),
      allocations: allocationRows,
    };
    try {
      setSaving(true);
      await api.create(payload);
      showSnackbar('Receipt created as DRAFT', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      setAllocations({});
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this receipt?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Receipt ${pastTense} successfully`, 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const openDetail = async (row) => {
    try {
      const res = await api.get(row.payment_id);
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch receipt'), 'error');
    }
  };

  const submitVoid = async () => {
    if ((voidDlg.void_reason?.trim() ?? '').length < 5) {
      showSnackbar('Void reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      await api.void(voidDlg.item.payment_id, { void_reason: voidDlg.void_reason.trim() });
      showSnackbar('Receipt voided successfully', 'success');
      setVoidDlg({ open: false, item: null, void_reason: '' });
      setDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const detailAllocations = Array.isArray(detail?.allocations) ? detail.allocations : [];

  return (
    <>
      <LedgerListPage
        resource={resource}
        title="Student Receipts"
        subtitle="AR payments received from students, allocated against open invoices"
        addLabel="New Receipt"
        idKey="payment_id"
        searchPlaceholder="Search receipt #, student..."
        emptyMessage="No receipts found."
        refreshKey={refreshKey}
        fetchItems={(params) => api.list(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setAllocations({}); setShowCreate(true); } : null}
        filters={[
          { key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) },
          { type: 'picker', key: 'customer_id', label: 'Student', picker: CustomerPicker },
          { type: 'daterange', keyFrom: 'start_date', keyTo: 'end_date', label: 'Payment date' },
          { type: 'numberrange', keyFrom: 'min_amount', keyTo: 'max_amount', label: 'amount' },
        ]}
        columns={[
          { label: 'Receipt #', render: (p) => <strong>{p.payment_number || `RCT-${p.payment_id}`}</strong> },
          { label: 'Student', render: (p) => studentLabel(p, look) },
          { label: 'Date', render: (p) => fmtDate(p.payment_date) },
          { label: 'Amount', render: (p) => formatAccountingAmount(p.amount_received ?? p.amount), align: 'right' },
          { label: 'Status', render: (p) => <ArStatusChip status={p.status} /> },
        ]}
        rowActions={(p) => {
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(p) }];
          if (p.status === 'DRAFT' && canApprove) {
            actions.push({ icon: ApproveIcon, title: 'Approve', color: 'primary', onClick: () => runWorkflow('Approve', 'approved', () => api.approve(p.payment_id)) });
          }
          if (p.status === 'APPROVED' && canApprove) {
            actions.push({ icon: PostIcon, title: 'Post', color: 'primary', onClick: () => runWorkflow('Post', 'posted', () => api.post(p.payment_id)) });
          }
          if (['DRAFT', 'APPROVED', 'POSTED'].includes(p.status) && canApprove) {
            actions.push({ icon: VoidIcon, title: 'Void', color: 'error', onClick: () => setVoidDlg({ open: true, item: p, void_reason: '' }) });
          }
          if (p.status === 'DRAFT' && canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete draft', color: 'error',
              onClick: () => confirm({
                title: 'Delete receipt',
                message: `Delete DRAFT receipt ${p.payment_number || `RCT-${p.payment_id}`}? This cannot be undone.`,
                onConfirm: async () => {
                  try {
                    await api.remove(p.payment_id);
                    showSnackbar('Receipt deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="lg" fullWidth>
        <DialogTitle>New Student Receipt</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1, mb: 2 }}>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <CustomerPicker label="Student" value={values.customer_id} onChange={(id) => setValues((p) => ({ ...p, customer_id: id }))} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField
                fullWidth size="small" type="date" label="Payment Date" value={values.payment_date}
                onChange={(e) => setValues((p) => ({ ...p, payment_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField
                fullWidth size="small" type="number" label="Amount Received" value={values.amount_received}
                onChange={(e) => setValues((p) => ({ ...p, amount_received: e.target.value }))}
                inputProps={{ min: 0.01, step: 'any' }} required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <BankAccountPicker label="Received Into" value={values.received_into_account_id} onChange={(id) => setValues((p) => ({ ...p, received_into_account_id: id }))} required />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" gutterBottom>Allocate to open invoices</Typography>
          {!values.customer_id ? (
            <Typography variant="body2" color="text.secondary">Select a student to load their open invoices.</Typography>
          ) : loadingInvoices ? (
            <Typography variant="body2" color="text.secondary">Loading open invoices...</Typography>
          ) : openInvoices.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No open invoices for this student.</Typography>
          ) : (
            <>
              {openInvoices.map((inv) => (
                <Box key={inv.invoice_id} display="flex" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ flex: '1 1 220px' }}>
                    {inv.invoice_number || `INV-${inv.invoice_id}`} - Balance {formatAccountingAmount(inv.balance_due)}
                  </Typography>
                  <TextField
                    size="small" type="number" label="Allocate"
                    value={allocations[inv.invoice_id] ?? ''}
                    onChange={(e) => setAllocation(inv.invoice_id, e.target.value)}
                    inputProps={{ min: 0, max: inv.balance_due, step: 'any' }}
                    sx={{ width: 140 }}
                  />
                </Box>
              ))}
              <Typography variant="body2" sx={{ mt: 1 }} fontWeight={700}>
                Allocated: {formatAccountingAmount(totalAllocated)} of {formatAccountingAmount(values.amount_received || 0)}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Receipt {detail?.payment_number || (detail?.payment_id ? `RCT-${detail.payment_id}` : '')}</DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {studentLabel(detail, look)} - {fmtDate(detail.payment_date)}
              </Typography>
              <DataTable
                columns={['Invoice', 'Allocated Amount']}
                data={detailAllocations}
                loading={false}
                emptyMessage="No allocations."
                renderRow={(a, i) => (
                  <TableRow key={a.allocation_id ?? i}>
                    <TableCell>{a.invoice?.invoice_number || `INV-${a.invoice_id}`}</TableCell>
                    <TableCell align="right">{formatAccountingAmount(a.allocated_amount)}</TableCell>
                  </TableRow>
                )}
              />
              <Box display="flex" justifyContent="flex-end" gap={3} sx={{ mt: 1 }}>
                <Typography variant="body2">Amount: <strong>{formatAccountingAmount(detail.amount_received ?? detail.amount)}</strong></Typography>
                <ArStatusChip status={detail.status} />
              </Box>
              {detail.status === 'VOID' && detail.void_reason && (
                <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                  Voided: {detail.void_reason}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={voidDlg.open}
        onClose={() => setVoidDlg({ open: false, item: null, void_reason: '' })}
        title={`Void receipt ${voidDlg.item?.payment_number || (voidDlg.item ? `RCT-${voidDlg.item.payment_id}` : '')}`}
        onSubmit={submitVoid}
        values={voidDlg}
        onChange={(key, value) => setVoidDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'void_reason', label: 'Void Reason (min 5 chars)', type: 'textarea', required: true, md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default PaymentsTab;
